// exportCheck — what the Studio's Export button says, and what each row of
// its issues list opens (PS-54). Pure. The issues themselves are the
// server's projection (`Packaging.Issues`); this only maps them onto the
// button and onto the thing a row opens, each case explicit.

export type { ExportButton };
export { exportButtonOf, issueTarget };

import { exhaustSwitchCase } from "@bilda/utils";
import type { ProjectIssue, ProjectIssues } from "../../app/ports/project.js";

type ExportButton =
    // Asked; no answer yet.
    | { readonly type: "checking" }
    // No answer, and no connection to ask.
    | { readonly type: "offline" }
    | { readonly type: "nothing_yet" }
    | { readonly type: "ready" }
    | { readonly type: "issues"; readonly count: number };

// The last answer stays while offline (the list still shows).
const exportButtonOf = (
    answer: ProjectIssues | undefined,
    online: boolean,
): ExportButton => {
    if (answer === undefined)
        return online ? { type: "checking" } : { type: "offline" };
    switch (answer.type) {
        case "project_nothing_yet":
            return { type: "nothing_yet" };
        case "project_ready":
            return { type: "ready" };
        case "project_has_issues":
            return { type: "issues", count: answer.total };
        default:
            return exhaustSwitchCase(answer);
    }
};

// The resource a row opens: the container its parts are in, the draft, the
// material with proposals. A goal has no place in the Studio yet (the
// coverage lens), and an unknown kind names nothing: no target.
const issueTarget = (issue: ProjectIssue): string | undefined => {
    switch (issue.type) {
        case "issue_parts_planned":
        case "issue_parts_empty":
        case "issue_parts_removed":
            return issue.container.id;
        case "issue_draft_unreviewed":
            return issue.part.id;
        case "issue_proposals_open":
            return issue.material.id;
        case "issue_objectives_not_covered":
        case "unknown":
            return undefined;
        default:
            return exhaustSwitchCase(issue);
    }
};
